export {useBoolState} from "@dvrt/graphql-widgets";

import {QueryHookOptions, QueryResult, useApolloClient} from "@apollo/client";
import {defineMessage} from "react-intl";
import {DocumentNode} from "graphql";
import {QueryItemBase, useGenericQuery, GenericQueryOptions, createUsePageTitle} from "@dvrt/graphql-widgets";

import {Features, OutgoingCallBarring, Query} from "./api";
import {
    useDomainNameQuery as _useDomainNameQuery,
    useDomainDefaultLanguageQuery as _useDomainDefaultLanguageQuery,
    useSystemLanguagesQuery,
    useDomainOutgoingCallBarringsQuery as _useDomainOutgoingCallBarringsQuery,
    DomainOutgoingCallBarringsQueryHookResult,
} from "./queries.generated";
import {TypedUseSelectorHook, useDispatch, useSelector} from "react-redux";
import type {RootState, AppDispatch} from "./store";
import {useCallback, useMemo, useRef, useState} from "react";
import {HideHook} from "./PageDescriptors";
import {useStructureConfig} from "./context/StructureConfigProvider";
import {useLocation, useNavigate} from "react-router";

export const usePageTitle = createUsePageTitle(defineMessage({id: "app_title", defaultMessage: "Concerto"}));

// typed dispatch instead of `useDispatch`
export const useAppDispatch = () => useDispatch<AppDispatch>();
// typed dispatch instead of `useSelector`
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Use the `useGenericQuery` hook in Concerto context.
 *
 * Params are the same as `useGenericQuery`.
 */
export function useConcertoGenericQuery<TItemsType extends QueryItemBase>(
    nodeName: keyof Query,
    itemsContentFragment: DocumentNode,
    options?: GenericQueryOptions,
) {
    const client = useApolloClient();
    return useGenericQuery<TItemsType>(nodeName, itemsContentFragment, client, options);
}

export function useDomainNameQuery(domainId: number) {
    const query = _useDomainNameQuery({
        variables: {id: domainId},
    });

    return {
        ...query,
        domainName: query.data?.domains.items[0]?.name,
    };
}

export type DomainOutgoingCallBarrings = {
    operator: OutgoingCallBarring[];
    admin: OutgoingCallBarring[];
};

export type DomainOutgoingCallBarringsQueryHook = DomainOutgoingCallBarringsQueryHookResult & {
    domainOutgoingCallBarrings: DomainOutgoingCallBarrings;
};

export function useDomainOutgoingCallBarringsQuery(domainId: number): DomainOutgoingCallBarringsQueryHook {
    const query = _useDomainOutgoingCallBarringsQuery({
        variables: {domainId},
    });

    const domainOutgoingCallBarrings: DomainOutgoingCallBarrings = useMemo(() => {
        const result = query.data?.domains.items[0];
        return {
            operator: result?.outgoingCallBarringOperator ?? [],
            admin: result?.outgoingCallBarringAdmin ?? [],
        };
    }, [query.data?.domains.items]);

    return {
        ...query,
        domainOutgoingCallBarrings,
    };
}

export function useDomainDefaultLanguageQuery(domainId: number) {
    const query = _useDomainDefaultLanguageQuery({
        variables: {domainId},
    });

    return {
        ...query,
        defaultLanguage: query.data?.domains.items[0]?.defaultLanguage,
    };
}

export function useDefaultLanguage(options?: {domainId?: number}) {
    const domainQuery = _useDomainDefaultLanguageQuery({
        variables: {domainId: options?.domainId},
        skip: !options?.domainId,
    });
    const systemQuery = useSystemLanguagesQuery();

    if (domainQuery.loading || systemQuery.loading)
        return {
            defaultLanguage: "en",
            loading: true,
        };

    return {
        loading: false,
        defaultLanguage:
            domainQuery.data?.domains.items[0]?.defaultLanguage ??
            systemQuery.data?.structureConfig.languages[0] ??
            "en",
    };
}

/**
 * A hook that returns a debounced state.
 * It returns the real value, the debounced value, and a function to set the value.
 * The setter returned also accepts a callback that will be called after the value is debounced.
 *
 * @param initialValue - The initial value of the state. Just like with `useState`.
 * @param delay - The delay in milliseconds before the value is debounced.
 *
 * @returns A tuple with the real value, the debounced value,
 * and a function to set the value along with a debounced callback.
 **/
export function useDebounceState<T>(initialValue: T, delay: number) {
    const [realValue, setRealValue] = useState(initialValue);
    const [debouncedValue, setDebouncedValue] = useState(initialValue);
    const timeoutHandler = useRef<NodeJS.Timeout>(undefined);

    const setValue = useCallback(
        (value: T, afterDebounce?: () => void) => {
            setRealValue(value);
            if (timeoutHandler.current) {
                clearTimeout(timeoutHandler.current);
            }
            timeoutHandler.current = setTimeout(() => {
                setDebouncedValue(value);
                afterDebounce?.();
            }, delay);
        },
        [delay],
    );

    return [realValue, debouncedValue, setValue] as const;
}

interface MightHaveOffsetAndLimit {
    offset?: number;
    limit?: number;
}

interface HasTotalCount {
    totalCount: number;
}

type FieldsWithTotalCount<T> = {
    [K in keyof T]: T[K] extends HasTotalCount ? K : never;
}[keyof T];

type PaginatedQueryResult<Query, Variables> = {
    query: QueryResult<Query, Variables>;
    totalCount: number;
    loading: boolean;

    page: number;
    pageSize: number;

    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
};

/**
 * Utility hook to provide handy pagination for queries with offset and limit.
 *
 * See `AuditLogsPage` for a working example.
 **/
export function usePaginatedQuery<
    Query,
    ProvidedVariables extends MightHaveOffsetAndLimit,
    Field extends FieldsWithTotalCount<Query>,
>(
    queryHook: (
        baseOptions: QueryHookOptions<Query, ProvidedVariables> &
            ({variables: ProvidedVariables; skip?: boolean} | {skip: boolean}),
    ) => QueryResult<Query, ProvidedVariables>,
    options: QueryHookOptions<Query, Omit<ProvidedVariables, "offset" | "limit">>,
    fieldName: Field,
): PaginatedQueryResult<Query, ProvidedVariables> {
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);

    // Cast through unknown because we intentionally extend variables with paging fields (offset, limit)
    const baseOptions: QueryHookOptions<Query, ProvidedVariables> & {variables: ProvidedVariables} = {
        ...(options as unknown as QueryHookOptions<Query, ProvidedVariables>),
        variables: {
            ...(options.variables as ProvidedVariables),
            offset,
            limit,
        },
    };

    const query = queryHook(baseOptions);

    const totalCount = (query.data?.[fieldName] as HasTotalCount)?.totalCount ?? 0;
    const page = Math.floor(offset / limit);

    const setPage = useCallback(
        (page: number) => {
            if (page < 0 || page >= Math.ceil(totalCount / limit)) {
                return;
            }
            setOffset(page * limit);
        },
        [limit, totalCount],
    );

    if (query.loading)
        return {
            query,
            totalCount,
            loading: true,
            page,
            pageSize: limit,
            setPage,
            setPageSize: setLimit,
        };

    return {
        query,
        totalCount,
        loading: false,
        page,
        pageSize: limit,
        setPage,
        setPageSize: setLimit,
    };
}

/**
 * Returns a `HideHook` that checks if a feature is enabled or not.
 **/
export function useFeatureHide(feature: keyof Features): HideHook {
    function hook() {
        const config = useStructureConfig();
        return !config.isEnabledFeature(feature);
    }

    return hook;
}

type Navigator = {
    /** Pop [count] (default 1) segments from the end of the current path and navigate to it. */
    popSegment: (count?: number) => void;
};

/**
 * A hook that provides navigation utilities.
 * This is an experimental hook, so if we find it useless, we can remove it later.
 * */
export function useNavigator(): Navigator {
    const navigate = useNavigate();
    const location = useLocation();

    const popSegment = useCallback(
        (count = 1) => {
            const segments = location.pathname.split("/").filter(Boolean);
            if (segments.length <= count) {
                navigate("/", {replace: true});
            } else {
                const newPath = "/" + segments.slice(0, -count).join("/");
                navigate(newPath, {replace: true});
            }
        },
        [location.pathname, navigate],
    );

    return {popSegment};
}
